import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteInvoiceConceptResult,
  InvoiceConcept,
  InvoiceConceptApiItem,
  UpsertInvoiceConceptRequest,
} from './invoice-concept.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class InvoiceConceptService {
  private readonly endpoint = apiUrl('InvoiceConcept');

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  getInvoiceConcepts(): Observable<InvoiceConcept[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.extractRows(response).map((item) => this.normalizeInvoiceConcept(item))),
      map((rows) => rows.sort((left, right) => left.name.localeCompare(right.name))),
    );
  }

  createInvoiceConcept(payload: UpsertInvoiceConceptRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, {
      ...payload,
      createdBy: this.resolveCurrentUser(),
    });
  }

  updateInvoiceConcept(invoiceConceptId: number, payload: UpsertInvoiceConceptRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${invoiceConceptId}`, {
      ...payload,
      modifiedBy: this.resolveCurrentUser(),
    });
  }

  deleteInvoiceConcept(invoiceConceptId: number): Observable<DeleteInvoiceConceptResult> {
    return this.http.delete<void>(`${this.endpoint}/${invoiceConceptId}`).pipe(
      map((): DeleteInvoiceConceptResult => ({ kind: 'success' })),
      catchError((error: HttpErrorResponse) => of<DeleteInvoiceConceptResult>({
        kind: 'error',
        message: error.status === 409
          ? 'This invoice concept is still referenced by vendors or invoices and cannot be removed.'
          : 'Unable to delete invoice concept at this time. Please try again.',
      })),
    );
  }

  private normalizeInvoiceConcept(item: InvoiceConceptApiItem): InvoiceConcept {
    return {
      id: this.toNumber(item.id),
      code: this.toText(item.code),
      name: this.toText(item.name),
    };
  }

  private extractRows(response: unknown): InvoiceConceptApiItem[] {
    if (Array.isArray(response)) {
      return response as InvoiceConceptApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as InvoiceConceptApiItem[];
      }

      if (Array.isArray(items)) {
        return items as InvoiceConceptApiItem[];
      }
    }

    return [];
  }

  private resolveCurrentUser(): string {
    const active = this.msalService.instance.getActiveAccount();
    if (active?.username) {
      return active.username;
    }

    const first = this.msalService.instance.getAllAccounts()[0];
    return first?.username ?? 'unknown.user';
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
